<template>
    <transition name="fade">
        <div class="approval-modal">
            <div class="modal-backdrop" @click="closeByBackdrop">
                <div class="modal-content relative mb-10">
                    <InnerLoader v-if="isLoading" />

                    <div class="modal-header mb-3">
                        <h5 class="modal-title font-size-22 my-0 shadow-text-none text-center">
                            {{ $t('submit_approval_modal_header') }}
                        </h5>
                        <div class="close">
                            <i
                                v-if="showCloseButton"
                                @click="closeModal"
                                class="fas text-grey fa-times text-xl shadow-text-none cursor-pointer"
                            ></i>
                        </div>
                    </div>
                    <div class="modal-body">
                        <div class="max-w-md mx-auto">
                            <div class="flex flex-col mb-5">
                                <label class="font-size-14 mb-2">{{
                                    $t('submit_approval_modal_select_category')
                                }}</label>
                                <tag-select
                                    :tags="quiz.tags"
                                    @change="onSelectTagHandler"
                                    :placeholder="$t('submit_approval_modal_placeholder_select_tag_categorize')"
                                    class="input input-block input-text-center quiz-dropdown category-select x-select"
                                />
                            </div>
                            <div class="flex flex-col mb-5">
                                <label class="font-size-14 mb-6">{{ $t('submit_approval_modal_age') }}</label>
                                <slider-bar
                                    :value="quiz.age != undefined ? quiz.age : [0, 99]"
                                    :min="0"
                                    :max="99"
                                    :dotSize="22"
                                    color="#000"
                                    @change="ageRecommend"
                                ></slider-bar>
                            </div>
                            <div class="flex flex-col mb-5">
                                <label class="font-size-14 mb-6">{{
                                    $t('submit_approval_modal_difficult_level')
                                }}</label>
                                <slider-bar
                                    :value="quiz.difficult != undefined ? quiz.difficult : 5"
                                    :min="0"
                                    :max="10"
                                    :dotSize="22"
                                    :rangeText="[
                                        $t('submit_approval_modal_slider_easy'),
                                        $t('submit_approval_modal_slider_normal'),
                                        $t('submit_approval_modal_slider_hard')
                                    ]"
                                    color="#000"
                                    @change="difficultLevel"
                                ></slider-bar>
                            </div>
                            <ul class="list-reset mb-5">
                                <li
                                    :key="index"
                                    v-for="(item, index) in validation"
                                    :class="{
                                        'shadow-text-none flex': true,
                                        'mb-2': true
                                    }"
                                >
                                    <i
                                        :class="{
                                            'fas fa-check-circle mr-2 font-size-17 text-green': item.status,
                                            'fas fa-times-circle mr-2 font-size-17 text-red': !item.status
                                        }"
                                        style="margin-top: 2px"
                                    ></i>
                                    <span :class="['font-size-15', item.textColor ? item.textColor : '']">{{
                                        item.message
                                    }}</span>
                                </li>
                                <li class="shadow-text-none flex">
                                    <i
                                        :class="{
                                            'fas fa-info-circle mr-2 font-size-17 text-yellow-dark': true
                                        }"
                                        style="margin-top: 2px"
                                    ></i>
                                    <span class="font-size-15">{{
                                        $t('submit_approval_modal_validate_correct_spell')
                                    }}</span>
                                </li>
                            </ul>
                            <div class="flex mb-3">
                                <x-checkbox
                                    :value="ckeckUnderstanding"
                                    @input="e => (this.ckeckUnderstanding = e.target.checked)"
                                    type="checkbox"
                                    class="mr-1"
                                    size="lg"
                                    theme="dark"
                                />

                                <p class="font-size-12 shadow-text-none">
                                    {{ $t('submit_approval_modal_understand') }}
                                </p>
                            </div>
                            <p
                                class="font-size-15 shadow-text-none mb-8 text-center"
                                v-html="$t('submit_approval_modal_see_contributors')"
                            ></p>
                            <div class="flex justify-center flex-wrap">
                                <x-button
                                    class="btn font-opensans font-size-17 btn-space-sm mr-3"
                                    :disabled="!isAlreadySubmit || !ckeckUnderstanding"
                                    @click="onSubmit()"
                                    >{{ $t('submit_approval_modal_button_submit') }}</x-button
                                >
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import { mapState } from 'vuex'
import InnerLoader from '@/components/InnerLoader'
import QuizValidation from '@/lib/quiz-validation'
import TagSelect from '@/components/TagSelect'
import SliderBar from '@/components/SliderBar'
import XButton from '@/components/v2/XButton'
import XCheckbox from '@/components/v2/XCheckbox'
import helperMixins from '@/mixins/helper.js'

export default {
    name: 'approval-popup',

    mixins: [helperMixins],

    components: {
        InnerLoader,
        TagSelect,
        SliderBar,
        XButton,
        XCheckbox
    },

    props: {
        showCloseButton: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            isLoading: true,
            validation: [],
            isAlreadySubmit: false,
            ckeckUnderstanding: false
        }
    },

    computed: {
        ...mapState({
            quiz: state => state.question
        })
    },

    watch: {
        ckeckUnderstanding() {
            this.validateQuiz()
        }
    },

    mounted() {
        this.validateQuiz()
    },

    methods: {
        async validateQuiz() {
            try {
                const quizValidation = new QuizValidation(this.quiz, this.translate)

                this.validation = await quizValidation.validation()

                this.isAlreadySubmit = !this.validation.filter(item => !item.status).length ? true : false
            } finally {
                this.isLoading = false
            }
        },

        closeByBackdrop(e) {
            if (!e.target.classList.contains('modal-backdrop')) {
                return
            }

            this.closeModal()
        },

        closeModal() {
            this.$emit('close')
        },

        onSubmit() {
            this.$emit('submit')
            this.closeModal()
        },

        onSelectTagHandler(tags) {
            this.$store.commit('updateQuizTags', tags)
            this.validateQuiz()
        },

        ageRecommend(age) {
            this.$store.commit('updateQuizAge', age)
            this.validateQuiz()
        },

        difficultLevel(difficult) {
            this.$store.commit('updateQuizDifficult', difficult)
            this.validateQuiz()
        }
    }
}
</script>

<style lang="scss">
$input-border-color: #cbcbcb;
$input-border-darker-color: #b6b6b6;

.approval-modal {
    z-index: 999;

    .modal-backdrop {
        background-color: rgba(0, 0, 0, 0.2);
        width: 100%;
        height: 100%;
        position: fixed;
        top: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: start;
        font-size: 0.5rem;
        z-index: 9999;
        overflow: auto;

        .modal-content {
            background-color: #fff;
            margin-top: 4rem;
            padding: 1rem;
            min-width: 720px;
            max-width: 720px;
            overflow: hidden;
            border-radius: 3px;
            box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.6);

            @media screen and (max-width: 719px) {
                min-width: 90%;
                margin: 1rem 0.5rem;
            }

            .modal-header {
                .close {
                    position: absolute;
                    right: 1rem;
                    top: 1rem;
                }
            }

            .modal-body {
                margin: 0;
            }
        }
    }
}
</style>

<style lang="scss">
@import '@/scss/variable.scss';

.category-select {
    border: 1px solid $input-border-color;
}
</style>
